<template>
    <div class="pdf-editor card">
        <div class="card-header">
            <h3 v-if="isFileUploaded">PDF Preview</h3>
            <h3 v-if="isCreatingDocument">Create PDF</h3>
            <h3 v-if="!isFileUploaded && !isCreatingDocument">PDF Viewer</h3>
        </div>
        <div v-if="documentExists" class="pdf-viewer-container card-body">
            <div v-if="isFileUploaded" class="pdf-viewer">
                <div class="zoom-controls">
                    <button @click="changeZoom(-0.2)">-</button>
                    <input type="range" min="0.9" max="2" step="0.2" v-model="zoom" />
                    <button @click="changeZoom(0.2)">+</button>
                    <button @click="printPdf">Save</button>
                    <button @click="printPdf">Imprimir</button>
                    <button @click="deleteDocument">Eliminar</button>
                </div>
                <div v-if="isRendering">Cargando páginas...</div>
                <div v-if="pdf" class="pdf-canvas-container" @mousedown="startDrag" @mousemove="onDrag"
                    @mouseup="stopDrag" @mouseleave="stopDrag" ref="container">
                    <div class="scroll-content" ref="content">
                        <canvas v-for="(page, index) in pages" :key="index" ref="pdfCanvas"></canvas>
                    </div>
                </div>
            </div>
            <div v-else class="pdf-viewer">
                <div>
                    <button @click="viewAllPages">Ver todo el documento</button>
                    <button @click="sendToPrint">Enviar a imprimir</button>
                </div>
                <ul>
                    <li v-for="(page, index) in createdPages" :key="index">
                        <span>Página {{ index + 1 }}</span>
                        <button @click="editPage(index)">Editar</button>
                        <button @click="deletePage(index)">Eliminar</button>
                        <button @click="viewPrintMode(index)">Vista de impresión</button>
                    </li>
                </ul>
            </div>
        </div>
        <div v-else class="card-body">
            <div class="initial-buttons">
                <button @click="loadDocument" class="initial-button import-button">
                    Import PDF
                    <i class="fas fa-file-import"></i>
                </button>
                <button @click="createDocument" class="initial-button create-button" disabled>
                    Create PDF
                    <i class="far fa-file"></i>
                </button>
            </div>
        </div>

        <!-- Formulario de creación de documento -->
        <div v-if="isCreatingDocument">
            <input type="text" v-model="docName" placeholder="Nombre del documento" required />
            <div>
                <label for="pageType">Tipo de hoja:</label>
                <select v-model="pageType">
                    <option value="A4">A4</option>
                    <option value="A3">A3</option>
                    <option value="Carta">Carta</option>
                </select>
            </div>
            <div>
                <input type="file" @change="uploadImages" multiple />
                <div v-if="uploadedImages.length > 0">
                    <h4>Imágenes subidas:</h4>
                    <ul>
                        <li v-for="(image, index) in uploadedImages" :key="index">
                            <img :src="image" alt="Imagen" />
                            <button @click="removeImage(index)">Eliminar</button>
                        </li>
                    </ul>
                </div>
            </div>
            <button @click="finishCreation">Finalizar creación</button>
        </div>
    </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import pdfWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export default {
    name: 'EditorWrapper',
    data() {
        return {
            documentExists: false,
            isFileUploaded: false,
            isCreatingDocument: false,
            pdf: null,
            pageNumber: 1,
            zoom: 1,
            isRendering: false,
            createdPages: [],
            uploadedImages: [],
            docName: '',
            pageType: 'A4',
            pages: [],
            isDragging: false,
            startX: 0,
            scrollLeft: 0,
        };
    },
    mounted() {
    },
    methods: {
        startDrag(e) {
            this.isDragging = true;
            this.$refs.container.style.cursor = 'grabbing';
            this.startX = e.pageX - this.$refs.content.offsetLeft;
            this.scrollLeft = this.$refs.content.scrollLeft;
        },
        stopDrag() {
            this.isDragging = false;
            this.$refs.container.style.cursor = 'grab';
        },
        onDrag(e) {
            if (!this.isDragging) return;
            e.preventDefault();
            const x = e.pageX - this.$refs.content.offsetLeft;
            const walk = (x - this.startX) * 1.5;
            this.$refs.content.scrollLeft = this.scrollLeft - walk;
        },
        async loadDocument() {
            const fileInput = document.createElement('input');
            fileInput.type = 'file';
            fileInput.accept = 'application/pdf';
            fileInput.onchange = async (e) => {
                const file = e.target.files[0];
                if (file && file.type === 'application/pdf') {
                    this.$store.commit("core/setLoadingModal", true);
                    const fileReader = new FileReader();
                    fileReader.onload = async (e) => {
                        try {
                            const typedarray = new Uint8Array(e.target.result);
                            this.pdf = await pdfjsLib.getDocument({ data: typedarray }).promise;
                            this.pages = Array.from({ length: this.pdf.numPages }, (_, i) => i + 1);
                            this.isFileUploaded = true;
                            this.documentExists = true;
                            await this.$nextTick();
                            this.renderAllPages();
                            this.$store.commit("core/setLoadingModal", false);
                        } catch (error) {
                            console.error('Error cargando el PDF:', error);
                            this.$store.commit("core/setLoadingModal", false);
                            alert('No se pudo cargar el PDF. Verifica el archivo.');
                        }
                    };
                    fileReader.readAsArrayBuffer(file);
                }
            };
            fileInput.click();
        },
        createDocument() {
            this.isCreatingDocument = true;
        },
        uploadImages(event) {
            const files = event.target.files;
            for (let i = 0; i < files.length; i++) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    this.uploadedImages.push(e.target.result);
                };
                reader.readAsDataURL(files[i]);
            }
            event.target.value = null; // Limpia el input
        },
        removeImage(index) {
            this.uploadedImages.splice(index, 1);
        },
        finishCreation() {
            if (this.docName.trim() && this.uploadedImages.length > 0) {
                this.createdPages.push({
                    name: this.docName.trim(),
                    images: this.uploadedImages,
                    pageType: this.pageType,
                });
                this.uploadedImages = [];
                this.docName = '';
                this.isCreatingDocument = false;
                this.documentExists = true;
            } else {
                alert('Por favor, ingrese un nombre y suba al menos una imagen.');
            }
        },
        async renderPage(num) {
            if (this.isRendering || !this.pdf) return;
            this.isRendering = true;
            try {
                const page = await this.pdf.getPage(num);
                const viewport = page.getViewport({ scale: this.zoom });
                await this.$nextTick();
                const canvas = this.$refs.pdfCanvas?.[num - 1];
                if (!canvas || typeof canvas.getContext !== 'function') {
                    console.warn(`Canvas no disponible o inválido para la página ${num}`);
                    return;
                }
                const context = canvas.getContext('2d');
                canvas.height = viewport.height;
                canvas.width = viewport.width;
                context.clearRect(0, 0, canvas.width, canvas.height);
                await page.render({ canvasContext: context, viewport }).promise;
            } catch (error) {
                console.error(`Error renderizando página ${num}:`, error);
            }
            this.isRendering = false;
        },
        async renderAllPages() {
            for (let i = 1; i <= this.pages.length; i++) {
                await this.renderPage(i);
            }
        },
        changeZoom(amount) {
            const currentZoom = parseFloat(this.zoom) || 1;
            this.zoom = Math.max(0.2, Math.min(currentZoom + amount, 2));
            if (parseFloat(this.zoom) < 0.8) this.zoom = 0.9;
            if (parseFloat(this.zoom) > 2) this.zoom = 2;
            this.renderAllPages();
        },
        deleteDocument() {
            this.pdf = null;
            this.isFileUploaded = false;
            this.documentExists = false;
        },
        printPdf() {
            window.print();
        },
        sendToPrint() {
            window.print();
        },
        viewAllPages() {
            alert('Mostrando todas las páginas del documento');
        },
        editPage(index) {
            alert(`Editando página ${index + 1}`);
        },
        deletePage(index) {
            this.createdPages.splice(index, 1);
        },
        viewPrintMode(index) {
            alert(`Vista de impresión de página ${index + 1}`);
        },
    },
    watch: {
        zoom() {
            this.renderAllPages();
        }
    }
};
</script>

<style scoped>
.pdf-editor {
    width: 100%;
    display: flex;
    flex-direction: column;
    padding-top: 0px !important;
    padding-right: 0px !important;
}

.pdf-viewer-container {
    width: 100%;
    margin: 20px auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 0px !important;
}

.pdf-viewer {
    width: 100%;
}

.pdf-canvas-container {
    overflow: hidden;
    width: 100%;
    max-width: 760px;
    cursor: grab;
    user-select: none;
}

.scroll-content {
    display: flex;
    gap: 16px;
    border: 1px solid #ccc;
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    scroll-behavior: smooth;
}

.scroll-content::-webkit-scrollbar {
    display: none;
}

.pdf-viewer canvas {
    width: fit-content;
    height: auto;
}

button {
    margin: 5px;
}

input[type="file"] {
    margin-top: 10px;
}

ul {
    list-style-type: none;
    padding: 0;
}

li {
    margin: 5px 0;
}

img {
    width: 50px;
    height: 50px;
    object-fit: cover;
}

.pdf-viewer-container {}

.initial-buttons {
    display: flex;
    justify-content: center;
    gap: 50px;
}

.initial-button {
    height: 150px;
    width: 150px;
    border: 1px solid #ccc;
    box-shadow: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background: #aaa;
    color: #fff;
    border-radius: 4px;
    font-size: 1.3rem;
    font-weight: bold;
    text-shadow: none;
}

.initial-button:hover {
    background: #888;
    color: #fff;
    box-shadow: 0 0 5px #000;
    cursor: pointer;
}

.initial-button:focus {
    outline: none;
    box-shadow: 0 0 0px #000;
}

.initial-button i {
    font-size: 3rem;
    margin-top: 20px;
}

.import-button {
    background-color: #137eff;
    border-color: #137eff;
}

.create-button {
    /* background-color: #28a745; */
    /* border-color: #28a745; */
}

@media (max-width: 1190px) {
    .pdf-canvas-container {
        max-width: 100%;
    }

    .pdf-viewer-container {
        width: 100%;
    }
}
</style>
